import React, { useState, useRef, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  IconButton,
  Typography,
  alpha,
  Fade,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Close,
  PhotoCamera,
  Refresh,
  CheckCircle,
  CreditCard,
  DirectionsBike,
  Person,
  CameraAlt,
  VideocamOff,
  Cameraswitch,
} from "@mui/icons-material";

import MDButton from "shared/components/MDButton";

const CameraCapture = ({ onCapture, onClose, tipo }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const [capturedImage, setCapturedImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [devices, setDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState(null);
  const [facingMode, setFacingMode] = useState("environment");

  const getTipoLabel = () => {
    switch (tipo) {
      case "cedula":
        return "Cédula";
      case "bicicleta":
        return "Bicicleta";
      case "usuario":
        return "Usuario";
      default:
        return "Foto";
    }
  };

  const getTipoIcon = () => {
    switch (tipo) {
      case "cedula":
        return <CreditCard sx={{ fontSize: 48, color: "#1a237e" }} />;
      case "bicicleta":
        return <DirectionsBike sx={{ fontSize: 48, color: "#1a237e" }} />;
      case "usuario":
        return <Person sx={{ fontSize: 48, color: "#1a237e" }} />;
      default:
        return <PhotoCamera sx={{ fontSize: 48, color: "#1a237e" }} />;
    }
  };

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  }, []);

  const startStream = useCallback(
    async (deviceId = null, facing = null) => {
      setLoading(true);
      setError(null);

      stopStream();

      try {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          throw new Error(
            "Tu navegador no soporta acceso a la cámara. Usa Chrome, Edge o Firefox actualizado."
          );
        }

        const constraints = {
          audio: false,
          video: deviceId
            ? { deviceId: { exact: deviceId } }
            : { facingMode: facing || facingMode },
        };

        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        streamRef.current = stream;

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }

        const allDevices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = allDevices.filter((d) => d.kind === "videoinput");
        setDevices(videoDevices);

        if (!deviceId && videoDevices.length > 0) {
          const currentDeviceId = stream.getVideoTracks()[0]?.getSettings()?.deviceId;
          if (currentDeviceId) setSelectedDeviceId(currentDeviceId);
        }

        setLoading(false);
      } catch (err) {
        console.error("Error al acceder a la cámara:", err);
        let message = "No se pudo acceder a la cámara.";

        if (err.name === "NotAllowedError") {
          message =
            "Permiso denegado. Habilita el acceso a la cámara en la configuración del navegador.";
        } else if (err.name === "NotFoundError") {
          message = "No se encontró ninguna cámara conectada.";
        } else if (err.name === "NotReadableError") {
          message = "La cámara está siendo usada por otra aplicación.";
        } else if (err.message) {
          message = err.message;
        }

        setError(message);
        setLoading(false);
      }
    },
    [facingMode, stopStream]
  );

  useEffect(() => {
    startStream();

    return () => {
      stopStream();
    };
  }, []);

  const handleCapture = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    const width = video.videoWidth;
    const height = video.videoHeight;

    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, width, height);

    const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
    setCapturedImage(dataUrl);

    stopStream();
  };

  const handleRetake = async () => {
    setCapturedImage(null);
    await startStream(selectedDeviceId);
  };

  const handleConfirm = () => {
    if (capturedImage && onCapture) {
      onCapture(capturedImage);
    }
    stopStream();
  };

  const handleClose = () => {
    stopStream();
    if (onClose) onClose();
  };

  const handleSwitchCamera = async () => {
    const nextFacing = facingMode === "environment" ? "user" : "environment";
    setFacingMode(nextFacing);
    setSelectedDeviceId(null);
    await startStream(null, nextFacing);
  };

  const handleDeviceChange = async (e) => {
    const deviceId = e.target.value;
    setSelectedDeviceId(deviceId);
    await startStream(deviceId);
  };

  return (
    <Dialog
      open={true}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      TransitionComponent={Fade}
      PaperProps={{
        sx: { borderRadius: "16px", overflow: "hidden" },
      }}
    >
      <DialogTitle sx={{ bgcolor: alpha("#1a237e", 0.04), py: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box sx={{ bgcolor: alpha("#1a237e", 0.1), p: 1, borderRadius: "8px" }}>
              <CameraAlt sx={{ color: "#1a237e" }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight="bold">
                Foto de {getTipoLabel()}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Usa la cámara para capturar la imagen
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={handleClose} size="small" sx={{ color: "#64748b" }}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3, bgcolor: "#000" }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: "8px" }}>
            {error}
          </Alert>
        )}

        <Box
          sx={{
            position: "relative",
            width: "100%",
            bgcolor: "#111",
            borderRadius: 2,
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 360,
            maxHeight: 500,
          }}
        >
          {!capturedImage && (
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{
                width: "100%",
                height: "auto",
                maxHeight: 500,
                objectFit: "contain",
                display: loading ? "none" : "block",
              }}
            />
          )}

          {capturedImage && (
            <img
              src={capturedImage}
              alt="Captura"
              style={{
                width: "100%",
                height: "auto",
                maxHeight: 500,
                objectFit: "contain",
              }}
            />
          )}

          {loading && !error && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                gap: 2,
              }}
            >
              <CircularProgress sx={{ color: "#fff" }} />
              <Typography variant="body2" sx={{ color: "#fff" }}>
                Iniciando cámara...
              </Typography>
            </Box>
          )}

          {error && !loading && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                gap: 2,
                p: 4,
              }}
            >
              <VideocamOff sx={{ fontSize: 64, color: "#ef5350" }} />
              <Typography variant="body1" sx={{ color: "#fff", textAlign: "center" }}>
                No se puede acceder a la cámara
              </Typography>
              <Button
                variant="outlined"
                onClick={() => startStream(selectedDeviceId)}
                startIcon={<Refresh />}
                sx={{ color: "#fff", borderColor: "#fff" }}
              >
                Reintentar
              </Button>
            </Box>
          )}

          <canvas ref={canvasRef} style={{ display: "none" }} />
        </Box>

        {/* Selector de cámara si hay varias */}
        {devices.length > 1 && !capturedImage && !loading && !error && (
          <FormControl fullWidth size="small" sx={{ mt: 2, bgcolor: "#fff", borderRadius: 1 }}>
            <InputLabel id="camera-select-label">Seleccionar cámara</InputLabel>
            <Select
              labelId="camera-select-label"
              value={selectedDeviceId || ""}
              label="Seleccionar cámara"
              onChange={handleDeviceChange}
            >
              {devices.map((device, idx) => (
                <MenuItem key={device.deviceId} value={device.deviceId}>
                  {device.label || `Cámara ${idx + 1}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          p: 2.5,
          gap: 1,
          bgcolor: "#fafafa",
          justifyContent: "space-between",
        }}
      >
        <MDButton
          onClick={handleClose}
          variant="outlined"
          startIcon={<Close />}
          sx={{ borderRadius: 1, color: "#64748b", borderColor: "#cbd5e1" }}
        >
          Cancelar
        </MDButton>

        <Box display="flex" gap={1}>
          {!capturedImage && devices.length > 1 && (
            <MDButton
              onClick={handleSwitchCamera}
              variant="outlined"
              startIcon={<Cameraswitch />}
              disabled={loading || !!error}
              sx={{ borderRadius: 1, color: "#1a237e", borderColor: "#1a237e" }}
            >
              Cambiar cámara
            </MDButton>
          )}

          {!capturedImage && (
            <MDButton
              onClick={handleCapture}
              variant="contained"
              startIcon={<PhotoCamera />}
              disabled={loading || !!error}
              sx={{
                borderRadius: 1,
                bgcolor: "#1a237e",
                color: "#ffffff !important",
                px: 4,
                py: 1,
                "&:hover": { bgcolor: "#0d1445" },
                "&.Mui-disabled": { bgcolor: alpha("#1a237e", 0.3) },
              }}
            >
              Capturar
            </MDButton>
          )}

          {capturedImage && (
            <>
              <MDButton
                onClick={handleRetake}
                variant="outlined"
                startIcon={<Refresh />}
                sx={{ borderRadius: 1, color: "#1a237e", borderColor: "#1a237e" }}
              >
                Repetir
              </MDButton>
              <MDButton
                onClick={handleConfirm}
                variant="contained"
                startIcon={<CheckCircle />}
                sx={{
                  borderRadius: 1,
                  bgcolor: "#2e7d32",
                  color: "#ffffff !important",
                  "&:hover": { bgcolor: "#1b5e20" },
                }}
              >
                Aceptar Foto
              </MDButton>
            </>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

CameraCapture.propTypes = {
  onCapture: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  tipo: PropTypes.oneOf(["cedula", "bicicleta", "usuario"]).isRequired,
};

export default CameraCapture;
